import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { mfaStore } from '@hds/identity-client-sdk';

// The Two-step sign-in section of Sign-in & security, the same on a phone and a desktop:
// whether the authenticator app is on, setting it up (a code to scan, and the first six digits
// back), and turning it off. The page owns the confirm it shows turning it off in — a sheet on a
// phone, a drawer on a desktop — and hands its state in here, as Active sessions does.
//
// A refusal answers on the page, in words, never a toast (identity effort ruling 2026-09-21).
// Nothing here ever shows or keeps a password: the person is signed in, and the identity
// server makes the calls for them.

// How the server's word for a refusal is said to the person.
const REFUSALS = Object.freeze({
    MFA_CODE_WRONG: 'authentication.mfa.wrongCode',
    SIGN_IN_AGAIN: 'authentication.mfa.signInAgain'
});

export function useSecondFactor({ confirm, busy }) {
    const { t } = useI18n();
    const mfa = mfaStore();

    const code = ref('');
    const qrImage = ref('');
    const loaded = ref(false);

    const isSettingUp = computed(() => mfa.setup !== null);
    const message = computed(() => {
        if (mfa.error === null) {
            return '';
        }
        if (REFUSALS[mfa.error] !== undefined) {
            return t(REFUSALS[mfa.error]);
        }
        return t(loaded.value ? 'authentication.mfa.failed' : 'authentication.mfa.loadFailed');
    });

    onMounted(async () => {
        await mfa.refresh();
        loaded.value = mfa.error === null;
    });

    // The code to scan is drawn here, from the address the server made, and goes when the set-up
    // does. It runs at once too: the store outlives the page, so a person who leaves mid-set-up
    // and comes back finds the secret still there, and needs its code drawn again.
    watch(() => mfa.setup, async (setup) => {
        qrImage.value = setup === null ? '' : await QRCode.toDataURL(setup.otpauthUri, { margin: 1, width: 192 });
    }, { immediate: true });

    async function startSetup() {
        code.value = '';
        await mfa.begin();
    }

    async function finishSetup() {
        const isOn = await mfa.enable(code.value.trim());
        if (isOn) {
            code.value = '';
        }
    }

    function cancelSetup() {
        code.value = '';
        mfa.cancelSetup();
    }

    function askTurnOff() {
        confirm.value = {
            open: true,
            title: t('authentication.mfa.turnOffTitle'),
            message: t('authentication.mfa.turnOffMessage'),
            confirmLabel: t('authentication.mfa.turnOffConfirm'),
            cancelLabel: t('authentication.mfa.cancelButton'),
            destructive: false,
            slide: false,
            error: '',
            action: turnOff
        };
    }

    // A failure answers inside the open confirm, and confirming again retries.
    async function turnOff() {
        busy.value = true;
        confirm.value.error = '';
        await mfa.disable();
        if (mfa.error === null) {
            confirm.value.open = false;
        } else {
            confirm.value.error = t(REFUSALS[mfa.error] ?? 'authentication.mfa.failed');
        }
        busy.value = false;
    }

    return {
        available: computed(() => mfa.available),
        enabled: computed(() => mfa.enabled),
        setup: computed(() => mfa.setup),
        working: computed(() => mfa.loading),
        loaded,
        isSettingUp,
        message,
        code,
        qrImage,
        startSetup,
        finishSetup,
        cancelSetup,
        askTurnOff
    };
}
